import { isMobile, ensureNumber } from "./index";
import { getTransitionDurationFromElement } from "../util/index";
import Carousel from "../carousel";
import EventHandler from "../dom/event-handler";
import Manipulator from "../dom/manipulator";
import SelectorEngine from "../dom/selector-engine";
import ExtendedSwipe from "./SwipeExtended";

/**
 * Constants
 */

const NAME = "carousel";
const DATA_KEY = "bs.carousel";
const DATA_API_KEY = ".data-api";
const DATA_ITEMS = "items";
const DATA_CLASS = "class";

const EVENT_KEY = `.${DATA_KEY}`;
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
const EVENT_LOAD_DATA_API = `load${EVENT_KEY}${DATA_API_KEY}`;
const EVENT_SLIDE = `slide${EVENT_KEY}`;

const CLASS_NAME_ACTIVE = "active";
const CLASS_NAME_CAROUSEL = "carousel";

const SELECTOR_ACTIVE = ".active";
const SELECTOR_DATA_SLIDE = "[data-slide], [data-slide-to]";
const SELECTOR_DATA_RIDE = '[data-bs-ride="owl"]';

const PROPERTY_DURATION = "--carousel-animation-duration";
const DURATION = 350;

const DefaultType = {
	...Carousel.DefaultType,
	class: "string",
	cycle: "boolean",
};

const Default = {
	...Carousel.Default,
	class: NAME,
	cycle: false,
};

class MultiItemCarousel extends Carousel {
	constructor(element, config) {
		super(element, config);

		this._duration =
			getTransitionDurationFromElement(this._element) || DURATION;

		this._config.class =
			`${Manipulator.getDataAttribute(
				this._element,
				DATA_CLASS,
			)}__${NAME}` || this._config.class;

		this._dots = SelectorEngine.find(
			`.${this._config.class}-dot`,
			this._element,
		);
		this._items = SelectorEngine.find(
			`.${this._config.class}-item`,
			this._element,
		);

		this._isSibling = true;
		this._isActive = false;
		this._dotClicked = null;

		this._handleMobileDevices();
		this._handleItemsCount();

		this._addEventListeners();
	}

	cycle() {
		if (this._isActive) {
			return;
		}

		if (!this._config.cycle) {
			return;
		}
		super.cycle();
	}

	_handleMobileDevices() {
		if (isMobile()) {
			this._resetActiveDot();

			this._isActive = true;
		}
	}

	isCarouselActive() {
		if (
			this._element.classList.contains(`${this._config.class}-active`)
		) {
			return false;
		} else {
			return true;
		}
	}

	_resetActiveDot() {
		this._dots.forEach((dot) =>
			dot.classList.remove(CLASS_NAME_ACTIVE),
		);

		this._dots[0].classList.add(CLASS_NAME_ACTIVE);
	}

	_maybeActive() {
		if (this.isCarouselActive()) {
			this._element.classList.add(`${this._config.class}-active`);
		}
	}

	_addEventListeners() {
		super._addEventListeners();
	}

	_addTouchEventListeners() {
		super._addTouchEventListeners();

		const startCallBack = () => {
			this._dotClicked = false;
			this._maybeActive();
			this._setPropertyDuration(this._duration);
		};

		const updatedConfig = {
			startCallback: startCallBack,
			...this._swipeHelper._config,
		};

		this._swipeHelper = new ExtendedSwipe(this._element, updatedConfig);
	}

	_keydown(event) {
		this._maybeActive();
		this._dotClicked = false;
		this._setPropertyDuration(this._duration);
		super._keydown(event);
		this._dots.forEach(
			(dot) => dot.classList.contains(CLASS_NAME_ACTIVE) && dot.focus(),
		);
	}

	_cloneItems(itemsList) {
		const minPerSlide = Manipulator.getDataAttribute(
			this._element,
			DATA_ITEMS,
		);
		itemsList.forEach((item) => {
			let next = item.nextElementSibling;
			for (let i = 1; i < minPerSlide; i++) {
				if (!next) next = itemsList[0];
				const clone = next.cloneNode(true);
				item.appendChild(clone.children[0]);
				next = next.nextElementSibling;
			}
		});
		const lastItem = itemsList[itemsList.length - 1];
		itemsList[0].parentNode.insertBefore(lastItem, itemsList[0]);
		itemsList.forEach((item) => item.classList.remove("active"));
		lastItem.classList.add("active");
	}

	_cloneDots(dots) {
		dots.forEach((dot, index) => {
			const clone = dot.cloneNode(true);
			if (clone.classList.contains(CLASS_NAME_ACTIVE)) {
				clone.classList.remove(CLASS_NAME_ACTIVE);
			}
			clone.setAttribute("data-slide-to", index + 2);
			clone.setAttribute("aria-label", `Slide ${index + 3}`);
			clone.classList.add("inactive");
			this._indicatorsElement.appendChild(clone);
		});
	}

	_handleInactiveDots(updatedDots) {
		EventHandler.on(this._element, EVENT_SLIDE, (e) => {
			switch (true) {
				case e.to <= 1:
					updatedDots[0].classList.remove("inactive");
					updatedDots[1].classList.remove("inactive");
					updatedDots[2].classList.add("inactive");
					updatedDots[3].classList.add("inactive");
					break;
				case e.to >= 2:
					updatedDots[0].classList.add("inactive");
					updatedDots[1].classList.add("inactive");
					updatedDots[2].classList.remove("inactive");
					updatedDots[3].classList.remove("inactive");
					break;

				default:
					break;
			}
		});
	}

	_handleItemsCount() {
		switch (true) {
			case this._items.length <= 1:
				this._dots.forEach((dot) => (dot.style.display = "none"));
				this._element.classList.add(
					`${this._config.class}-${CLASS_NAME_ACTIVE}`,
				);
				break;

			case this._items.length === 2:
				let updatedItems = [...this._items];

				this._items.forEach((item) => {
					const clone = item.cloneNode(true);
					clone.classList.remove(CLASS_NAME_ACTIVE);
					item.parentElement.appendChild(clone);
					updatedItems.push(clone);
				});
				this._cloneItems(updatedItems);
				this._cloneDots(this._dots);
				const updatedDots = SelectorEngine.find(
					`.${this._config.class}-dot`,
					this._element,
				);
				this._handleInactiveDots(updatedDots);
				this._items = updatedItems;
				this._dots = updatedDots;

				break;

			default:
				this._cloneItems(this._items);
				break;
		}
	}

	_setPropertyDuration(duration) {
		this._element.style.setProperty(PROPERTY_DURATION, `${duration}ms`);
	}

	_setActiveIndicatorElement(index) {
		if (!this._indicatorsElement) {
			return;
		}

		index = ensureNumber(index);
		let dotCurrent = [...this._dots].findIndex((dot) =>
			dot.classList.contains(CLASS_NAME_ACTIVE),
		);
		const distance = Math.abs(dotCurrent - index);

		if (distance > 1 && this._dotClicked) {
			this._isSibling = false;
		} else {
			this._isSibling = true;
		}

		const activeIndicator = SelectorEngine.findOne(
			SELECTOR_ACTIVE,
			this._indicatorsElement,
		);

		if (this._isSibling) {
			activeIndicator.classList.remove(CLASS_NAME_ACTIVE);
			activeIndicator.removeAttribute("aria-current");
		}

		const newActiveIndicator = SelectorEngine.findOne(
			`[data-slide-to="${index}"]`,
			this._indicatorsElement,
		);

		if (newActiveIndicator && this._isSibling) {
			newActiveIndicator.classList.add(CLASS_NAME_ACTIVE);
			newActiveIndicator.setAttribute("aria-current", "true");
		}
	}

	_handleDots(index) {
		index = ensureNumber(index);
		let dotCurrent = [...this._dots].findIndex((dot) =>
			dot.classList.contains(CLASS_NAME_ACTIVE),
		);
		this._dotClicked = true;

		const distance = Math.max(1, Math.abs(dotCurrent - index));
		let transitionDuration = Math.max(50, this._duration / distance);
		this._setPropertyDuration(transitionDuration);

		this._setActiveIndicatorElement(index);

		if (dotCurrent !== null) {
			if (distance > 1) {
				const step = dotCurrent < index ? 1 : -1;
				for (let i = dotCurrent; i !== index + step; i += step) {
					setTimeout(() => this.to(i), Math.abs(i - dotCurrent) * 60);
				}
			} else {
				this.to(index);
			}
		}

		setTimeout(() => {
			this._setActiveIndicatorElement(index);
		}, 50);
	}

	nextWhenVisible() {
		this._setPropertyDuration(this._duration);
		super.nextWhenVisible();
	}

	static get Default() {
		return Default;
	}

	static get DefaultType() {
		return DefaultType;
	}
}

/**
 * Data API implementation
 */

EventHandler.on(
	document,
	EVENT_CLICK_DATA_API,
	SELECTOR_DATA_SLIDE,
	function (event) {
		const target = SelectorEngine.getElementFromSelector(this);

		if (!target || !target.classList.contains(CLASS_NAME_CAROUSEL)) {
			return;
		}

		event.preventDefault();

		const carousel = MultiItemCarousel.getOrCreateInstance(target);
		const slideIndex = this.getAttribute("data-slide-to");

		if (carousel._isSliding) {
			return;
		}

		if (slideIndex) {
			if (carousel.isCarouselActive()) {
				carousel._maybeActive();
			}
			carousel._handleDots(slideIndex);
			carousel._maybeEnableCycle();
			return;
		}

		if (this.getAttribute("data-slide") === "next") {
			carousel._setPropertyDuration(this._duration);
			carousel.next();
			carousel._maybeEnableCycle();
			return;
		}

		carousel._setPropertyDuration(this._duration);
		carousel.prev();
		carousel._maybeEnableCycle();
	},
);

EventHandler.on(window, EVENT_LOAD_DATA_API, () => {
	const carousels = SelectorEngine.find(SELECTOR_DATA_RIDE);

	for (const carousel of carousels) {
		MultiItemCarousel.getOrCreateInstance(carousel);
	}
});

export default MultiItemCarousel;
